/**
 * The production's main/backup pair as the node answering sees it (PLAN §8.6:
 * "shows which node is active per production"): this node's role, where
 * commands go, the term, whether a warm standby protects the show, any alarm
 * — and the manual switchover (§8.4), when the operator may ask for one.
 *
 * Props in, events out: the view passes the state's `redundancy` and turns a
 * click into the node's `POST /productions/{id}/switchover`.
 */
import type { PlayoutRedundancyWire } from '@acp/api-contract/playout.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';

export type PairBadgeProps = {
  readonly redundancy: PlayoutRedundancyWire;
  /** The operator holds `node.switchover` on the production's studio (and it has not ended). */
  readonly canSwitch: boolean;
  /** A switchover is under way. */
  readonly switching: boolean;
  readonly onSwitchover: () => void;
};

const PROTECTION: Readonly<Record<PlayoutRedundancyWire['protection'], { readonly variant: 'success' | 'warning' | 'offline'; readonly label: string }>> = {
  protected: { variant: 'success', label: 'Protected' },
  syncing: { variant: 'warning', label: 'Standby syncing' },
  unprotected: { variant: 'offline', label: 'Unprotected' },
};

export function PairBadge({ redundancy, canSwitch, switching, onSwitchover }: PairBadgeProps) {
  const active = redundancy.role === 'active';
  const protection = PROTECTION[redundancy.protection];
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-card px-4 py-1.5 text-sm" data-testid="pair-badge" data-role={redundancy.role} data-protection={redundancy.protection}>
      <Badge variant={active ? 'success' : 'standby'}>{active ? 'Active' : 'Standby'}</Badge>
      <span className="font-mono">{redundancy.node}</span>
      <span className="text-muted-foreground">
        {active ? `drives the devices · term ${String(redundancy.term)}` : `commands go to ${redundancy.activeNode ?? 'no active node yet'} · term ${String(redundancy.term)}`}
      </span>
      <Badge variant={protection.variant}>{protection.label}</Badge>
      {redundancy.manualOnly ? <Badge variant="outline">Manual failover only</Badge> : null}
      {redundancy.alarm === null ? null : (
        <span className="text-destructive" role="alert" data-testid="pair-alarm">
          {redundancy.alarm}
        </span>
      )}
      {canSwitch ? (
        <Button className="ml-auto" size="sm" variant="outline" disabled={switching || redundancy.peerNode === null} onClick={onSwitchover} data-testid="switchover">
          {switching ? 'Switching…' : `Switch over to ${active ? (redundancy.peerNode ?? 'the peer') : 'this node'}`}
        </Button>
      ) : null}
    </div>
  );
}
